import { useMemo } from 'react';
import Image from 'next/image';
import { getPercentSaved, convertPrice } from 'utils/price';
import amazon from 'public/amazon.png';
import amazonPrime from 'public/amazon-prime.png';
import { useCart } from 'utils/cart-context';
import formatCurrency from 'utils/format-currency';
import { type BuyFrom, BuyFromType, BuyFromAvailability } from 'constant';
import { onSelectBuyFrom } from 'utils/gtm';
import * as rudder from 'utils/rudderstack';
import useLocalCurrency from 'utils/useLocalCurrency';
import { type SalesFunnelData, useFunnelData } from 'utils/funnel-data-context';

import styles from './styles.module.scss';

import type { Offers, Media } from 'queries/graphql';

export default function FunnelCartBuyFrom({
  onClick,
  isSelected,
  offer: cartOffer,
  type,
  logo,
  brandName,
  buyFrom,
}: {
  onClick: (type: BuyFrom | string) => void;
  isSelected: boolean;
  offer: Offers;
  type: BuyFrom | string;
  logo?: Media;
  brandName?: string;
  buyFrom: any;
}) {
  const classNames = ['root', styles.main, isSelected ? styles.selected : styles.unselected];
  const { offersConfig } = useFunnelData<SalesFunnelData>();
  const { cartData, cartIsReady } = useCart();
  const { symbol: currencySymbol, rate: localFxRate } = useLocalCurrency();

  const offer = offersConfig.find(_offer => _offer.id === cartOffer.hashedId);

  // TODO: Move offer data quantity handling into context so it can be shared between
  // components. With that change, we would no longer need to use cart data in this component.
  const cartLineItem = useMemo(
    () => cartData?.data?.lineItems?.find(item => item.offerId === offer?.id),
    [cartData, offer?.id]
  );

  const quantityIsReady = cartIsReady && cartLineItem;

  const handleClick = () => {
    onClick(type);
    onSelectBuyFrom({ ...offer, quantity: quantityIsReady ? cartLineItem.quantity : 1 }, type);

    rudder.onSelectBuyFrom(
      { ...offer, quantity: quantityIsReady ? cartLineItem.quantity : 1 },
      type
    );
  };

  const percentageOff = useMemo(() => {
    if (!offer?.compareAtPrice || !offer?.price) {
      return null;
    }

    const percentSaved = getPercentSaved(offer.compareAtPrice, offer.price);
    return percentSaved.toFixed(0);
  }, [offer?.compareAtPrice, offer?.price]);

  const availability = useMemo(() => {
    if (type === BuyFromType.VELOCITY) {
      return BuyFromAvailability.AVAILABLE;
    }

    if (type === BuyFromType.AMAZON) {
      // If Buy From availability is set to available check Amazon Price and Amazon Link is also set
      if (buyFrom?.availability === BuyFromAvailability.AVAILABLE) {
        if (offer?.amazonPrice && offer?.amazonLink) {
          return BuyFromAvailability.AVAILABLE;
        }
        // Otherwise only use availability set on Buy From if it is defined and not set to Available
      } else if (buyFrom?.availability) {
        return buyFrom.availability;
      }
    }

    // Always assume not available as default
    return BuyFromAvailability.NOT_AVAILABLE;
  }, [type, offer, buyFrom]);

  return (
    <button
      className={classNames.join(' ')}
      onClick={handleClick}
      disabled={availability !== BuyFromAvailability.AVAILABLE}
    >
      <div className={styles.text_wrapper}>
        {type === BuyFromType.VELOCITY && (
          <div className={styles.logo_name}>
            <Image src={logo.src} alt={brandName} width={38} height={38} />
            <h5>{brandName}</h5>
          </div>
        )}
        {type === BuyFromType.AMAZON && (
          <div className={styles.logo_name}>
            <Image src={amazon} alt="Amazon" width={38} height={38} />
            <h5>Amazon.com</h5>
          </div>
        )}

        {availability === BuyFromAvailability.SOLD_OUT && <div>This product is sold out</div>}
        {availability === BuyFromAvailability.NOT_AVAILABLE && (
          <div>This product is not available</div>
        )}

        {availability === BuyFromAvailability.AVAILABLE && (
          <>
            {buyFrom && (
              <>
                {buyFrom.deliveryText && (
                  <span>
                    Free delivery: {buyFrom.deliveryText}
                    {type === BuyFromType.AMAZON && buyFrom.amazonPrime && (
                      <Image src={amazonPrime} alt="Amazon Prime" width={54} height={22} />
                    )}
                  </span>
                )}
                {buyFrom.returnsText && <div>Free returns: {buyFrom.returnsText}</div>}
                {buyFrom.tags && (
                  <div className={styles.tags_wrapper}>
                    {buyFrom.tags.map(tag => (
                      <span key={tag}>{tag}</span>
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
      {availability === BuyFromAvailability.AVAILABLE && (
        <div className={styles.price_wrapper}>
          {type === BuyFromType.AMAZON && (
            <p className={styles.price}>
              {!quantityIsReady && 'Calculating...'}
              {quantityIsReady &&
                formatCurrency(
                  convertPrice(offer?.amazonPrice, localFxRate) * cartLineItem.quantity,
                  false,
                  currencySymbol
                )}
            </p>
          )}
          {type === BuyFromType.VELOCITY && (
            <div className={styles.price_wrapper}>
              {!quantityIsReady && <p className={styles.price}>Calculating...</p>}
              {quantityIsReady && (
                <>
                  {percentageOff && <p className={styles.percentage_off}>-{percentageOff}%</p>}
                  <div>
                    <p className={styles.price}>
                      {formatCurrency(cartData.data.subtotal, false, currencySymbol)}
                    </p>
                    {offer?.compareAtPrice && (
                      <p className={styles.compareAtPrice}>
                        {formatCurrency(
                          convertPrice(offer?.compareAtPrice, localFxRate) * cartLineItem.quantity,
                          false,
                          currencySymbol
                        )}
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </button>
  );
}
